import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { sortBy } from 'lodash';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { Guid } from 'guid-typescript';
import { ExtensionEditState, ExtensionGridEditConfig } from '../model/extension-grid-edit.model';
import { ExtensionService } from './extension.service';
import { DictionaryService } from './dictionary.service';
import { MessageHandlerService } from '../utility/message-handler.service';

@Injectable({
  providedIn: 'root'
})
export class ExtensionGridEditHelper
{
  constructor(
    private fb: UntypedFormBuilder,
    private extensionService: ExtensionService,
    private dictionaryService: DictionaryService,
    private messageHandlerService: MessageHandlerService)
  { }

  public addNew(config: ExtensionGridEditConfig): void
  {
    const newItem: any =
    {
      isNew: true,
      id: Guid.create().toString(),
      settingExtension: null,
      value: null,
      codeSetId: null,
      editing: false,
    };
    this.startEdit(config, newItem, true);
    if (!config.extensions.value)
    {
      config.extensions.setValue([]);
    }
    config.extensions.value.push(newItem);
    this.refreshGrid(config);
    config.afterAddNew?.();
  }

  public startEdit(config: ExtensionGridEditConfig, dataItem: any, isNew: boolean): void
  {
    const resolvedSettingExtension = this.resolveSettingExtension(config, dataItem.settingExtension);
    const codeSetId = this.getCodeSetId(config, dataItem, resolvedSettingExtension);
    const formGroup = this.fb.group(
      {
        isNew,
        id: dataItem.id,
        settingExtension: [{ value: resolvedSettingExtension ?? null, disabled: false }, Validators.required],
        value: [
          {
            value: isNew
              ? null
              : this.extensionService.parseExtensionValueForEdit(resolvedSettingExtension, dataItem.value),
            disabled: false
          },
          Validators.required],
        codeSetId: codeSetId ?? null,
      });

    const editState: ExtensionEditState =
    {
      formGroup,
      codeOptions: [],
      valueEditorVisible: true,
      valueEditorRenderKey: null,
      previousSettingId: isNew ? null : resolvedSettingExtension?.id ?? null,
      settingSubscription: null,
      valueSubscription: null,
    };

    dataItem.extensionEditState = editState;
    dataItem.editing = true;
    this.bindSettingChanges(config, dataItem, isNew, resolvedSettingExtension);
    this.configureValueControl(config, dataItem, resolvedSettingExtension);
    this.loadCodeOptions(config, dataItem, resolvedSettingExtension);
  }

  public startEditRow(config: ExtensionGridEditConfig, dataItem: any): void
  {
    if (dataItem.editing)
    {
      return;
    }

    this.startEdit(config, dataItem, false);
    this.loadSettingExtensions(config, false);
    this.refreshGrid(config);
  }

  public cancelRow(config: ExtensionGridEditConfig, dataItem: any): void
  {
    if (dataItem.isNew)
    {
      config.extensions.setValue(config.extensions.value.filter((extension: any) => extension.id !== dataItem.id));
    }
    else
    {
      dataItem.editing = false;
    }

    this.disposeEditState(dataItem);
    this.refreshGrid(config);
  }

  public deleteExtension(config: ExtensionGridEditConfig, extension: any): void
  {
    this.disposeEditState(extension);
    config.extensions.setValue(config.extensions.value.filter((c: any) => c.id !== extension.id));
    this.refreshGrid(config);
    config.markDirty();
  }

  public loadSettingExtensions(config: ExtensionGridEditConfig, reset: boolean): void
  {
    if (reset)
    {
      config.allSettingExtensions.length = 0;
      config.unusedSettingExtensions.length = 0;
    }

    this.extensionService.getExtensionsByCategory(config.category).subscribe({
      next: (data) =>
      {
        config.allSettingExtensions.splice(0, config.allSettingExtensions.length, ...data);
        this.cacheSettingExtensions(config, data);
        const extensionsInUse = config.viewExtensions.gridData.data;
        const unused = sortBy(
          config.allSettingExtensions.filter(
            (u: any) => !extensionsInUse.map((m: any) => m.settingExtension?.id).includes(u.id)), ['name']);
        config.unusedSettingExtensions.splice(0, config.unusedSettingExtensions.length, ...unused);
        config.extensions.value
          ?.filter((extension: any) => extension.extensionEditState)
          .forEach((extension: any) =>
          {
            const resolved = this.getSelectedSettingExtension(config, extension);
            this.configureValueControl(config, extension, resolved);
            if (this.extensionService.getExtensionDataType(resolved) === 'DICTIONARY')
            {
              this.loadCodeOptions(config, extension, resolved);
            }
          });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveRow(config: ExtensionGridEditConfig, dataItem: any): void
  {
    const editState = dataItem.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    const settingExtension = this.getSelectedSettingExtension(config, dataItem);
    this.enforceStringValueLength(config, dataItem, settingExtension);

    if (!editState.formGroup.valid)
    {
      editState.formGroup.get('value')?.markAsTouched();
      return;
    }

    const formGroup = editState.formGroup;
    dataItem.settingExtension =
      config.allSettingExtensions.find((x: any) => x.id === formGroup.get('settingExtension').value.id)
      ?? settingExtension;
    const rawExtensionValue = formGroup.get('value').value;
    dataItem.value = this.extensionService.serializeExtensionValueForSave(
      dataItem.settingExtension,
      rawExtensionValue);
    dataItem.codeSetId = dataItem.settingExtension?.codeSetId ?? null;
    config.applySaveRowFields?.(dataItem);
    if (this.extensionService.getExtensionDataType(dataItem.settingExtension) === 'DICTIONARY')
    {
      dataItem.valueDisplayName = this.extensionService.resolveExtensionDictionaryDisplayName(
        dataItem.settingExtension,
        rawExtensionValue,
        editState.codeOptions);
    }
    else
    {
      dataItem.valueDisplayName = null;
    }

    dataItem.isNew = false;
    dataItem.editing = false;
    this.disposeEditState(dataItem);
    this.refreshGrid(config);
    config.markDirty();
  }

  public formatExtensionValue(config: ExtensionGridEditConfig, dataItem: any): string
  {
    const settingExtension = this.getSelectedSettingExtension(config, dataItem);
    return this.extensionService.formatExtensionValue({
      ...dataItem,
      settingExtension: settingExtension ?? dataItem?.settingExtension
    });
  }

  public getDataType(config: ExtensionGridEditConfig, dataItem: any): string
  {
    return this.extensionService.getExtensionDataType(this.getSelectedSettingExtension(config, dataItem));
  }

  public getValueMaxLength(config: ExtensionGridEditConfig, dataItem: any): number
  {
    return this.extensionService.getExtensionStringMaxLength(this.getSelectedSettingExtension(config, dataItem));
  }

  public getValueInputKey(config: ExtensionGridEditConfig, dataItem: any): string
  {
    const settingExtension = this.getSelectedSettingExtension(config, dataItem);
    return `${settingExtension?.id ?? 'new'}-${this.getValueMaxLength(config, dataItem)}-${this.getDataType(config, dataItem)}`;
  }

  public getNumberFormat(config: ExtensionGridEditConfig, dataItem: any): string
  {
    return this.extensionService.getExtensionNumberFormat(this.getSelectedSettingExtension(config, dataItem));
  }

  public getNumberDecimals(config: ExtensionGridEditConfig, dataItem: any): number
  {
    return this.extensionService.getExtensionNumberDecimals(this.getSelectedSettingExtension(config, dataItem));
  }

  public isValueEditorVisible(dataItem: any): boolean
  {
    return dataItem?.extensionEditState?.valueEditorVisible ?? true;
  }

  public isTextAreaValue(config: ExtensionGridEditConfig, dataItem: any): boolean
  {
    return this.getDataType(config, dataItem) === 'TEXT_AREA';
  }

  public onValueInput(config: ExtensionGridEditConfig, event: Event, dataItem: any): void
  {
    if (!dataItem?.extensionEditState || !this.isStringValueEditor(config, dataItem))
    {
      return;
    }

    this.truncateValueInput(config, event.target as HTMLInputElement, dataItem);
  }

  public onValuePaste(config: ExtensionGridEditConfig, event: Event, dataItem: any): void
  {
    if (!dataItem?.extensionEditState || !this.isStringValueEditor(config, dataItem))
    {
      return;
    }

    const input = event.target as HTMLInputElement;
    setTimeout(() => this.truncateValueInput(config, input, dataItem));
  }

  public onSettingExtensionChange(config: ExtensionGridEditConfig, settingExtension: any, dataItem: any): void
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    const resolved = this.resolveSettingExtension(config, settingExtension);
    const settingExtensionControl = editState.formGroup.get('settingExtension');
    if (resolved && settingExtensionControl.value !== resolved)
    {
      settingExtensionControl.setValue(resolved, { emitEvent: false });
    }

    const previousId = editState.previousSettingId;
    const nextId = resolved?.id ?? null;
    if (nextId !== previousId)
    {
      editState.previousSettingId = nextId;
      editState.formGroup.get('value').setValue(null);
    }

    this.loadCodeOptions(config, dataItem, resolved);
    this.configureValueControl(config, dataItem, resolved);
    editState.formGroup.patchValue(
      { codeSetId: resolved?.codeSetId ?? null },
      { emitEvent: false });
  }

  public onValueKeydown(config: ExtensionGridEditConfig, event: KeyboardEvent, dataItem: any): void
  {
    if (!dataItem?.extensionEditState || !this.isStringValueEditor(config, dataItem))
    {
      return;
    }

    const maxLength = this.getValueMaxLength(config, dataItem);
    if (maxLength >= ExtensionService.EXTENSION_VALUE_DB_MAX_LENGTH)
    {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey)
    {
      return;
    }

    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'
    ];
    if (allowedKeys.includes(event.key))
    {
      return;
    }

    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const selectedLength = Math.max(0, selectionEnd - selectionStart);
    if (input.value.length - selectedLength >= maxLength)
    {
      event.preventDefault();
    }
  }

  public cacheSettingExtensionDefinition(config: ExtensionGridEditConfig, extension: any): void
  {
    if (extension?.id != null)
    {
      const existing = config.settingExtensionById.get(extension.id);
      config.settingExtensionById.set(extension.id, existing ? { ...existing, ...extension } : extension);
    }
  }

  public cacheSettingExtensions(config: ExtensionGridEditConfig, extensions: any[]): void
  {
    extensions?.forEach((extension) => this.cacheSettingExtensionDefinition(config, extension));
  }

  public resolveDictionaryDisplayNames(config: ExtensionGridEditConfig, extensions: any[]): void
  {
    if (!extensions?.length)
    {
      return;
    }

    const dictionaryExtensions = extensions.filter((extension) =>
    {
      const resolved = this.resolveSettingExtension(config, extension.settingExtension);
      return extension?.value &&
        this.getCodeSetId(config, extension, resolved) != null &&
        this.extensionService.getExtensionDataType(resolved) === 'DICTIONARY';
    });

    if (!dictionaryExtensions.length)
    {
      return;
    }

    forkJoin(
      dictionaryExtensions.map((extension) =>
      {
        const resolved = this.resolveSettingExtension(config, extension.settingExtension);
        const codeSetId = this.getCodeSetId(config, extension, resolved);
        return this.dictionaryService.getCachedCodesByCodeSetId(codeSetId).pipe(
          map((codes) =>
          {
            extension.valueDisplayName = this.extensionService.resolveExtensionDictionaryDisplayName(
              resolved,
              extension.value,
              codes);
            return extension;
          })
        );
      })
    ).subscribe({
      next: () =>
      {
        config.viewExtensions.setData(config.extensions?.value ?? extensions);
        config.changeDetectorRef.detectChanges();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private refreshGrid(config: ExtensionGridEditConfig): void
  {
    config.viewExtensions.setData([...config.extensions.value]);
    config.changeDetectorRef.detectChanges();
  }

  private bindSettingChanges(config: ExtensionGridEditConfig, dataItem: any, isEdit: boolean, initialSettingExtension: any): void
  {
    const editState = dataItem.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    editState.settingSubscription?.unsubscribe();
    editState.previousSettingId = isEdit ? initialSettingExtension?.id ?? null : null;

    editState.settingSubscription =
      editState.formGroup.get('settingExtension').valueChanges.subscribe({
        next: (settingExtension: any) =>
        {
          this.onSettingExtensionChange(config, settingExtension, dataItem);
        }
      });
  }

  private getSelectedSettingExtension(config: ExtensionGridEditConfig, dataItem: any): any
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (editState)
    {
      return this.resolveSettingExtension(config, editState.formGroup.get('settingExtension')?.value);
    }

    return this.resolveSettingExtension(config, dataItem?.settingExtension);
  }

  private resolveSettingExtension(config: ExtensionGridEditConfig, settingExtension: any): any
  {
    if (!settingExtension?.id)
    {
      return settingExtension;
    }

    const cached = config.settingExtensionById.get(settingExtension.id);
    if (cached)
    {
      return cached;
    }

    return config.allSettingExtensions.find((x: any) => x.id === settingExtension.id)
      ?? config.unusedSettingExtensions.find((x: any) => x.id === settingExtension.id)
      ?? settingExtension;
  }

  private configureValueControl(config: ExtensionGridEditConfig, dataItem: any, settingExtension: any): void
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    const resolved = this.resolveSettingExtension(
      config,
      settingExtension ?? editState.formGroup.get('settingExtension')?.value);
    this.applyValueControlSettings(config, dataItem, resolved);

    if (resolved?.id && resolved.dataType == null)
    {
      this.extensionService.getExtension(resolved.id).subscribe({
        next: (fullExtension) =>
        {
          this.cacheSettingExtensionDefinition(config, fullExtension);
          const refreshed = this.resolveSettingExtension(
            config,
            settingExtension ?? editState.formGroup.get('settingExtension')?.value);
          this.applyValueControlSettings(config, dataItem, refreshed);
          this.loadCodeOptions(config, dataItem, refreshed);
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else if (this.extensionService.getExtensionDataType(resolved) === 'DICTIONARY')
    {
      this.loadCodeOptions(config, dataItem, resolved);
    }
  }

  private applyValueControlSettings(config: ExtensionGridEditConfig, dataItem: any, resolved: any): void
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    const dataType = this.extensionService.getExtensionDataType(resolved);
    const maxLength = this.extensionService.getExtensionStringMaxLength(resolved);
    const renderKey = `${resolved?.id ?? 'new'}-${maxLength}-${dataType}`;
    const recreateEditor = renderKey !== editState.valueEditorRenderKey;
    editState.valueEditorRenderKey = renderKey;

    const valueControl = editState.formGroup.get('value');
    const validators: ValidatorFn[] = [this.getValueRequiredValidator(dataType)];
    if (this.extensionService.isExtensionStringDataType(dataType))
    {
      validators.push(Validators.maxLength(maxLength));
    }
    valueControl.setValidators(validators);
    this.enforceStringValueLength(config, dataItem, resolved);
    valueControl.updateValueAndValidity({ emitEvent: false });

    editState.valueSubscription?.unsubscribe();
    editState.valueSubscription = null;
    if (this.extensionService.isExtensionStringDataType(dataType))
    {
      editState.valueSubscription = valueControl.valueChanges.subscribe({
        next: (value: string) =>
        {
          const limit = this.extensionService.getExtensionStringMaxLength(
            this.resolveSettingExtension(config, editState.formGroup.get('settingExtension')?.value));
          if (typeof value === 'string' && value.length > limit)
          {
            valueControl.setValue(value.substring(0, limit), { emitEvent: false });
            this.syncValueInputMaxLength(config, dataItem);
          }
        }
      });
    }

    if (recreateEditor)
    {
      editState.valueEditorVisible = false;
      config.changeDetectorRef.detectChanges();
      setTimeout(() =>
      {
        editState.valueEditorVisible = true;
        config.changeDetectorRef.detectChanges();
        this.syncValueInputMaxLength(config, dataItem);
      });
      return;
    }

    config.changeDetectorRef.detectChanges();
    this.syncValueInputMaxLength(config, dataItem);
  }

  private enforceStringValueLength(config: ExtensionGridEditConfig, dataItem: any, settingExtension: any): void
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    const resolved = this.resolveSettingExtension(
      config,
      settingExtension ?? editState.formGroup.get('settingExtension')?.value);
    if (!this.extensionService.isExtensionStringDataType(this.extensionService.getExtensionDataType(resolved)))
    {
      return;
    }

    const valueControl = editState.formGroup.get('value');
    const maxLength = this.extensionService.getExtensionStringMaxLength(resolved);
    const current = valueControl.value;
    if (typeof current === 'string' && current.length > maxLength)
    {
      valueControl.setValue(current.substring(0, maxLength), { emitEvent: false });
      this.syncValueInputMaxLength(config, dataItem);
    }
  }

  private disposeEditState(dataItem: any): void
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    editState.settingSubscription?.unsubscribe();
    editState.valueSubscription?.unsubscribe();
    delete dataItem.extensionEditState;
  }

  private isStringValueEditor(config: ExtensionGridEditConfig, dataItem: any): boolean
  {
    return this.extensionService.isExtensionStringDataType(this.getDataType(config, dataItem));
  }

  private getValueRequiredValidator(dataType: string): ValidatorFn
  {
    if (this.extensionService.isExtensionSwitchOrCheckboxDataType(dataType))
    {
      return (control: AbstractControl): ValidationErrors | null =>
      {
        const value = control.value;
        return value === true || value === false ? null : { required: true };
      };
    }

    return Validators.required;
  }

  private truncateValueInput(config: ExtensionGridEditConfig, input: HTMLInputElement | HTMLTextAreaElement, dataItem: any): void
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    const maxLength = this.getValueMaxLength(config, dataItem);
    if (input.value.length <= maxLength)
    {
      return;
    }

    const truncated = input.value.substring(0, maxLength);
    input.value = truncated;
    const valueControl = editState.formGroup.get('value');
    if (valueControl?.value !== truncated)
    {
      valueControl.setValue(truncated, { emitEvent: false });
    }
  }

  private syncValueInputMaxLength(config: ExtensionGridEditConfig, dataItem: any): void
  {
    if (!this.isStringValueEditor(config, dataItem))
    {
      return;
    }

    const maxLength = this.getValueMaxLength(config, dataItem);
    const rowId = dataItem.id;
    setTimeout(() =>
    {
      const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        `[${config.valueRowIdAttr}="${rowId}"]`);
      fields.forEach((field) =>
      {
        field.maxLength = maxLength;
        this.truncateValueInput(config, field, dataItem);
      });
    });
  }

  private getCodeSetId(config: ExtensionGridEditConfig, dataItem: any, settingExtension: any): number | null
  {
    const resolved = this.resolveSettingExtension(config, settingExtension ?? dataItem?.settingExtension);
    const codeSetId = resolved?.codeSetId ?? dataItem?.codeSetId ?? dataItem?.extensionEditState?.formGroup?.get('codeSetId')?.value;
    if (codeSetId == null || codeSetId === '')
    {
      return null;
    }

    const numericCodeSetId = Number(codeSetId);
    return Number.isFinite(numericCodeSetId) ? numericCodeSetId : null;
  }

  private loadCodeOptions(config: ExtensionGridEditConfig, dataItem: any, settingExtension?: any): void
  {
    const editState = dataItem?.extensionEditState as ExtensionEditState;
    if (!editState)
    {
      return;
    }

    const resolved = this.resolveSettingExtension(
      config,
      settingExtension ?? editState.formGroup.get('settingExtension')?.value);
    const codeSetId = this.getCodeSetId(config, dataItem, resolved);

    if (!resolved ||
      this.extensionService.getExtensionDataType(resolved) !== 'DICTIONARY' ||
      codeSetId == null)
    {
      editState.codeOptions = [];
      return;
    }

    editState.formGroup.patchValue({ codeSetId }, { emitEvent: false });

    this.dictionaryService.getCachedCodesByCodeSetId(codeSetId).subscribe({
      next: (codes) =>
      {
        editState.codeOptions = codes ?? [];
        if (!dataItem.isNew && dataItem.value != null && dataItem.value !== '')
        {
          const parsedValue = this.extensionService.parseExtensionValueForEdit(resolved, dataItem.value);
          const valueControl = editState.formGroup.get('value');
          if (valueControl && valueControl.value !== parsedValue)
          {
            valueControl.setValue(parsedValue, { emitEvent: false });
          }
        }
        config.changeDetectorRef.detectChanges();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
